import { Component, OnInit, AfterViewInit, ElementRef, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { PartesService } from './partes.service';
import { partesTranslations, PartesTranslations } from './translations-partes';
import { getCurrentLanguage } from '../core/helpers/utils';
import { formatDateString } from '../core/helpers/utils';
import { ActivatedRoute } from '@angular/router';

// services
import { EventService } from 'src/app/core/service/event.service';
import { CasosService } from '../casos/casos.service'; // Importa el servicio del caso
import { AuthenticationService } from '../core/service/auth.service';


// constants
import { EventType } from 'src/app/core/constants/events';

// types
import { SortEvent } from 'src/app/shared/advanced-table/sortable.directive';
import { Column } from 'src/app/shared/advanced-table/advanced-table.component';
import { Parte } from './partes.model';

@Component({
  selector: 'app-list-partes',
  templateUrl: './list-partes.component.html',
  styleUrls: ['./list-partes.component.scss']
})
export class ListPartesComponent implements OnInit {

  @Output() changePartesView = new EventEmitter<{ view: string, id?: string }>();
  @Output() refreshCase = new EventEmitter<void>();

  constructor(
    private eventService: EventService,
    private router: Router,
    private route: ActivatedRoute,
    private elRef: ElementRef,
    private partesService: PartesService,
    private casoService: CasosService,
    private authService: AuthenticationService
  ) { }

  records: Parte[] = [];
  columns: Column[] = [];
  pageSizeOptions: number[] = [10, 25, 50, 100];

  currentLanguage: string = getCurrentLanguage();
  buttonTitle: string = 'Nueva parte';

  disableOptions: boolean = false;
  estadoExpediente: string | undefined;


  loaded: boolean = false;
  error: string = '';

  allDatasearch: Parte[] = [];

  showConfirmDeleteModal: boolean = false;
  parteToDelete: Parte | null = null;

  disableNoAdmin: boolean = true;

  ngAfterViewInit(): void { }

  ngOnInit(): void {
    const currentTranslations = partesTranslations[this.currentLanguage].TABLE;
    this.buttonTitle = currentTranslations.BUTTON;
  
    // Consultar el caso y cargar datos
    const id_expediente = this.route.snapshot.paramMap.get('id');
    if (id_expediente) {
      this.casoService.getCaso(id_expediente).subscribe({
        next: (expediente) => {
          this.estadoExpediente = expediente.estado;
          const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
          this.disableOptions = estadosCerrados.includes(this.estadoExpediente ?? '');
  
          // Cargar las partes directamente desde el expediente
          this.records = expediente.partes.sort((a: Parte, b: Parte) => {
            const tipoParteA = a.tipo_parte ?? '';
            const tipoParteB = b.tipo_parte ?? '';
            return this.compare(tipoParteB, tipoParteA);
          });
  
          this.allDatasearch = this.records;
          this.initTableCofig();
  
          this.authService.currentUser().subscribe(user => {
            if (user?.nivel_acceso) {
              if (user.nivel_acceso == "ADMIN") {
                this.disableNoAdmin = false;
              }
            }
            this.loaded = true;
          });
        },
        error: (error) => {
          console.error('Error al cargar el estado del caso:', error);
          this.error = error;
          this.loaded = false;
        }
      });
    }
  }
  


  _fetchData(): void {
    const id_expediente = this.route.snapshot.paramMap.get('id');

    if (id_expediente !== null) {
      this.partesService.getPartes(id_expediente).subscribe((res) => {
        // Ordenar por defecto por tipo_parte en orden descendente, manejando undefined
        this.records = res.sort((a, b) => {
          const tipoParteA = a.tipo_parte ?? '';  // Si es undefined, usar cadena vacía
          const tipoParteB = b.tipo_parte ?? '';  // Si es undefined, usar cadena vacía
          return this.compare(tipoParteB, tipoParteA);
        });
        this.allDatasearch = this.records;
        this.initTableCofig();
        this.loaded = true;
      },
        (error: string) => {
          this.error = error;
          this.loaded = false;
        });
    }
  }


  initTableCofig(): void {
    let currentLanguage: string = getCurrentLanguage()
    const currentTranslations = partesTranslations[currentLanguage].TABLE;
    this.columns = [
      {
        name: 'razon_social',
        label: currentTranslations.RAZON_SOCIAL,
        formatter: (record: Parte) => record.razon_social,
        width: 1400,
      },
      {
        name: 'tipo_parte',
        label: currentTranslations.TIPO_PARTE,
        formatter: (record: Parte) => record.tipo_parte,
        width: 200,
      },
      {
        name: 'impuesto',
        label: currentTranslations.IMPUESTO,
        formatter: (record: Parte) => 
          ((record.impuesto_aplicable?.toString()) + " %"),
        width: 50,
      },
      {
        name: 'imputable',
        label: currentTranslations.IMPUTABLE,
        formatter: (record: Parte) => 
           (this.formatDecimalForDisplay(record.porcentaje_imputable) + " %"),
        width: 50,
      },
      {
        name: 'accionesPartes',
        label: currentTranslations.ACTIONS,
        formatter: (record: Parte) => null,
        width: 200,
        sort: false
      }
    ];
  }

  compare(v1: string | number, v2: string | number): any {
    return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
  }

  formatDecimalForDisplay(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }
    return value.toString().replace(/\./g, ',');
  }

  onSort(event: SortEvent): void {
    if (event.direction === '') {
      this.records = this.allDatasearch;
    } else {
      this.records = [...this.records].sort((a, b) => {
        const res = this.compare(a[event.column] as string | number, b[event.column] as string | number);
        return event.direction === 'asc' ? res : -res;
      });
    }
  }

  normalizeText(text: string): string {
    return text.trim().toLocaleLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  matches(row: Parte, term: string) {
    return this.normalizeText(row.razon_social as string).includes(this.normalizeText(term))
  }

  searchData(searchTerm: string): void {
    if (searchTerm === '') {
      this.records = this.allDatasearch;
    }
    else {
      let updatedData = this.allDatasearch;

      updatedData = updatedData.filter(record => this.matches(record, searchTerm));
      this.records = updatedData;
    }
  }

  navigateToNewParte() {
    this.changePartesView.emit({ view: 'new' });
  }

  navigateToRepresentantes(parte: Parte) {
    this.changePartesView.emit({ view: 'representantes', id: parte.id });
  }


  navigateToEditParte(parte: Parte) {
    this.changePartesView.emit({ view: 'edit', id: parte.id });
  }

  openDeleteModal(parte: Parte) {
    this.parteToDelete = parte;
    this.showConfirmDeleteModal = true;
  }

  closeDeleteModal() {
    this.showConfirmDeleteModal = false;
    this.parteToDelete = null;
  }

  confirmDelete() {
    if (this.parteToDelete?.id) {
      this.partesService.deleteParte(this.parteToDelete.id).subscribe(() => {
        this._fetchData(); // Actualiza los datos después de borrar
        this.closeDeleteModal();
        this.refreshCase.emit();
      });
    }
  }
}
